import { useEffect, useRef, useState } from "react";
import { X, Loader2, ImageIcon, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function asUrl(u) {
  if (!u) return "";
  return u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

/**
 * AdminImageUploader
 *  - Cross-browser reliable upload: visible <button> → ref.current.click() → real file picker.
 *  - Single-file input (no `multiple` attribute) — multi-image mode lets the user
 *    upload images one at a time which is the most reliable cross-browser pattern
 *    AND avoids the form-save-before-upload race because each upload is a discrete action.
 *  - `onBusy(bool)` callback lets the parent disable Save while an upload is in flight.
 */
export default function AdminImageUploader({
  value,
  onChange,
  max = 1,
  label = "Image",
  testId = "img",
  onBusy,
}) {
  const isMulti = max > 1;
  const list = isMulti ? (Array.isArray(value) ? value : []) : value ? [value] : [];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    onBusy?.(busy);
  }, [busy, onBusy]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG / PNG / WebP / GIF).");
      return;
    }
    if (list.length >= max) {
      toast.error(`Maximum ${max} image${max > 1 ? "s" : ""} reached. Remove one to add another.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads-api/image", fd);
      const next = isMulti ? [...list, data.url] : data.url;
      onChange(next);
      toast.success(`Image uploaded (${(data.size / 1024).toFixed(1)} KB)`);
    } catch (err) {
      toast.error(`Upload failed: ${formatApiError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same filename
    if (f) upload(f);
  };

  const removeAt = (idx) => {
    if (isMulti) onChange(list.filter((_, i) => i !== idx));
    else onChange("");
  };

  const slotFull = list.length >= max;

  return (
    <div data-testid={`${testId}-uploader`}>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-2">
        {label}{" "}
        {isMulti && (
          <span className="text-slate-400 font-normal normal-case">
            ({list.length}/{max})
          </span>
        )}
      </div>
      <div className={`grid gap-3 ${isMulti ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1"}`}>
        {list.map((u, i) => (
          <div key={i} className="relative aspect-[4/3] bg-slate-100 border border-slate-200 group">
            <img src={asUrl(u)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`${testId}-remove-${i}`}
              className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {!slotFull && (
          <button
            type="button"
            disabled={busy}
            onClick={() => !busy && inputRef.current?.click()}
            data-testid={`${testId}-upload-btn`}
            className={`aspect-[4/3] border-2 border-dashed transition-colors grid place-items-center ${
              busy
                ? "border-slate-200 text-slate-400 cursor-not-allowed"
                : "border-slate-300 hover:border-amber-500 hover:bg-amber-50/30 text-slate-500 hover:text-amber-600 cursor-pointer"
            }`}
          >
            <div className="flex flex-col items-center gap-1.5">
              {busy ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <div className="relative">
                  <ImageIcon className="w-6 h-6" />
                  <Plus className="w-3 h-3 absolute -right-1.5 -bottom-1 bg-amber-500 text-white rounded-full p-[1px]" />
                </div>
              )}
              <span className="text-[11px] uppercase tracking-wider font-bold">
                {busy ? "Uploading…" : isMulti ? "Add Image" : "Upload Image"}
              </span>
              <span className="text-[10px] text-slate-400 normal-case font-normal">
                JPG · PNG · WebP · ≤ 8MB
              </span>
            </div>
          </button>
        )}
      </div>
      {/* Single-file input — most reliable across browsers and CDP test environments */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        onChange={onPick}
        data-testid={`${testId}-file-input`}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
    </div>
  );
}
